import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { Loader2 } from 'lucide-react';
import { Notification } from '../../../../../components/ui/custom-notification';
import { SearchableSelectInDialog } from '../../../../../components/ui/SearchableSelectInDialog';
import { formatWeldNumbersForTransferApi } from './weldSelection';
import { useProductionModalAudit } from '../../../../audit/useProductionModalAudit';
import { logWeldItemsAssignedToProject } from '../../../utils/productionAudit';

interface Project {
  id?: number | string;
  project_id?: number | string;
  projectName?: string;
  project_name?: string;
  projectNumber?: string;
  project_number?: string;
  name?: string;
}

function mapApiProjectRow(p: Record<string, unknown>): Project {
  const rawId = p.projectId ?? p.project_id ?? p.id;
  const id =
    typeof rawId === 'number' || typeof rawId === 'string' ? rawId : undefined;
  const rawName = p.projectName ?? p.project_name ?? p.name;
  const name = typeof rawName === 'string' ? rawName : undefined;
  const rawNumber = p.projectNumber ?? p.project_number;
  const projectNumber =
    typeof rawNumber === 'number' || typeof rawNumber === 'string'
      ? String(rawNumber).trim()
      : undefined;

  return {
    id,
    project_id: id,
    projectName: name,
    project_name: name,
    projectNumber,
    project_number: projectNumber,
    name,
  };
}

function formatProjectListLabel(project: Project): string {
  const name = getProjectNameFromRow(project);
  const num = project.projectNumber ?? project.project_number;
  const trimmed = num?.trim();
  return trimmed ? `${name} (${trimmed})` : name;
}

function getProjectNameFromRow(project: Project): string {
  return (
    project.projectName ||
    project.project_name ||
    project.name ||
    `Project ${project.id || project.project_id}`
  );
}

interface AssignToProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceProjectId: number | string;
  sourceProjectName?: string;
  selectedWeldNumbers: string[];
  selectedWeldAndJobNumbers?: string[];
  onSuccess?: () => void;
}

export const AssignToProjectModal: React.FC<AssignToProjectModalProps> = ({
  isOpen,
  onClose,
  sourceProjectId,
  sourceProjectName,
  selectedWeldNumbers,
  selectedWeldAndJobNumbers = [],
  onSuccess,
}) => {
  const reduxProjectList = useSelector((state: any) => state.project?.ProjectList);

  const weldNumbersForApi = formatWeldNumbersForTransferApi(
    selectedWeldNumbers,
    selectedWeldAndJobNumbers
  );

  useProductionModalAudit(
    isOpen,
    'Assign Weld Data',
    sourceProjectId,
    sourceProjectName,
    'production-assign-to-project'
  );

  const [selectedProjectName, setSelectedProjectName] = useState<string | undefined>();
  const [isAssigning, setIsAssigning] = useState(false);

  const limitsBaseUrl =
    process.env.REACT_APP_LIMITS_API_BASE_URL ||
    'https://by2e8b0nwd.execute-api.us-west-2.amazonaws.com/dev/v1';

  useEffect(() => {
    if (isOpen) {
      setSelectedProjectName(undefined);
    }
  }, [isOpen]);

  const projects = useMemo(() => {
    const list = Array.isArray(reduxProjectList) ? reduxProjectList : [];
    return list
      .map((row) => mapApiProjectRow(row as Record<string, unknown>))
      .filter((p) => p.id != null && String(p.id).trim() !== '');
  }, [reduxProjectList]);

  const getProjectName = (project: Project) => getProjectNameFromRow(project);

  const getProjectId = (project: Project) => project.id || project.project_id;

  const isSourceProject = (project: Project) => {
    const id = String(getProjectId(project));
    if (id && id === String(sourceProjectId)) return true;
    const name = getProjectName(project);
    if (
      sourceProjectName &&
      name.toLowerCase() === sourceProjectName.trim().toLowerCase()
    ) {
      return true;
    }
    return false;
  };

  const searchableProjectOptions = useMemo(
    () =>
      projects
        .filter((p) => !isSourceProject(p))
        .map((p) => {
          const label = formatProjectListLabel(p);
          const num = (p.projectNumber ?? p.project_number)?.trim();
          return {
            id: getProjectId(p)!,
            name: label,
            searchText: [getProjectName(p), num].filter(Boolean).join(' '),
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [projects, sourceProjectId, sourceProjectName]
  );

  const selectedProject = useMemo(
    () => projects.find((p) => formatProjectListLabel(p) === selectedProjectName),
    [projects, selectedProjectName]
  );

  const handleAssign = async () => {
    if (!selectedProject) return;
    const targetProjectId = getProjectId(selectedProject);
    const targetProjectName = getProjectName(selectedProject);
    const beforeWeldNumbers = [...weldNumbersForApi];
    const beforeWeldAndJobEntries =
      selectedWeldAndJobNumbers.length > 0
        ? [...selectedWeldAndJobNumbers]
        : [...beforeWeldNumbers];

    setIsAssigning(true);
    try {
      const response = await fetch(
        `${limitsBaseUrl}/weld-status/assign-weld-items-to-project`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source_project_id: sourceProjectId,
            target_project_id: targetProjectId,
            weld_numbers: beforeWeldNumbers,
          }),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message =
          result?.message || result?.data?.message || 'Assignment failed';
        throw new Error(message);
      }

      const apiData = result?.data?.data ?? result?.data ?? {};
      logWeldItemsAssignedToProject({
        sourceProjectId,
        sourceProjectName,
        targetProjectId: targetProjectId ?? '',
        targetProjectName,
        weldNumbers: beforeWeldNumbers,
        weldAndJobEntries: beforeWeldAndJobEntries,
        passViewRecordsMoved: apiData.pass_view_records_moved,
        weldJobCombinations: apiData.weld_job_combinations,
      });

      Notification.success(
        'Assign successful',
        `${beforeWeldNumbers.length} weld(s) assigned to ${targetProjectName}. Table will refresh.`
      );
      onSuccess?.();
      onClose();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to assign weld items to project';
      Notification.error(message);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleClose = () => {
    setSelectedProjectName(undefined);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="bg-white max-w-lg w-[90vw] gap-0 p-0 sm:rounded-lg">
        <DialogHeader className="px-4 sm:px-6 py-4 border-b border-[#E2E5EA] space-y-1">
          <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
            Assign Weld Data
          </DialogTitle>
          <p className="text-[13px] text-text-grey font-normal">
            {sourceProjectName ? (
              <>
                From <span className="font-medium text-blueprint-blue">{sourceProjectName}</span>
                {' · '}
                {selectedWeldNumbers.length} weld{selectedWeldNumbers.length !== 1 ? 's' : ''} selected
                {selectedWeldNumbers.length > 0 && selectedWeldNumbers.length <= 3 && (
                  <> ({selectedWeldNumbers.join(', ')})</>
                )}
              </>
            ) : (
              <>Select a target project for the chosen welds.</>
            )}
          </p>
        </DialogHeader>

        <div className="p-4 sm:p-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-[12px] font-medium text-text-grey">Target project</Label>
            <SearchableSelectInDialog
              value={selectedProjectName}
              onValueChange={setSelectedProjectName}
              placeholder="Assign to Project..."
              searchPlaceholder="Search projects..."
              options={searchableProjectOptions}
              disabled={isAssigning}
              className="w-full"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#E2E5EA]">
            <Button variant="outline" onClick={handleClose} disabled={isAssigning}>
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={!selectedProject || isAssigning || weldNumbersForApi.length === 0}
              className="bg-hero-red hover:bg-hero-red-60 text-white"
            >
              {isAssigning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning…
                </>
              ) : (
                'Assign to project'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
